import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Check, Plus, X, FileText } from "lucide-react";

const inputCls =
  "border-0 border-b border-slate-300 shadow-none rounded-none px-0 h-10 font-semibold text-[17px] bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#1F2937] placeholder:text-slate-400";

const LEGAL_ENTITIES = ["LLC", "Sole Proprietorship", "Corporation", "S-Corporation", "Partnership", "Non-Profit"];
const INDUSTRIES = [
  "Construction", "Retail", "Restaurant / Hospitality", "Healthcare",
  "Manufacturing", "Transportation / Logistics", "Professional Services",
  "Technology / SaaS", "Auto / Repair", "Wholesale", "E-commerce",
  "Real Estate", "Beauty / Salon", "Firearms dealer", "Other",
];
const PREMISES = ["Own", "Rent", "Mortgaged"];
const USE_OF_FUNDS = [
  "Working capital", "Buy inventory", "Equipment", "Expansion",
  "Payroll", "Marketing", "Refinance existing debt", "Real estate", "Other",
];

function Section({ eyebrow, title, description, children }) {
  return (
    <section className="border-t border-slate-200 pt-10">
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-4">
          <div className="small-caps text-slate-500">{eyebrow}</div>
          <h2 className="mt-3 font-semibold text-[28px] leading-tight text-[#1F2937]">{title}</h2>
          {description && <p className="mt-3 text-[14px] leading-[1.7] text-slate-600 max-w-sm">{description}</p>}
        </div>
        <div className="col-span-12 md:col-span-8 space-y-6">{children}</div>
      </div>
    </section>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <label className="small-caps text-slate-500">
          {label} {required && <span className="text-[#1F2937]">·</span>}
        </label>
        {hint && <span className="small-caps text-slate-400 text-[10px]">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function OwnerForm({ prefix, owner, onChange }) {
  const set = (k, v) => onChange({ ...owner, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Field label="Full legal name" required>
        <Input value={owner.full_name || ""} onChange={(e) => set("full_name", e.target.value)}
          data-testid={`${prefix}-name`} className={inputCls} placeholder="Jane Q. Founder" />
      </Field>
      <Field label="Email" required>
        <Input type="email" value={owner.email || ""} onChange={(e) => set("email", e.target.value)}
          data-testid={`${prefix}-email`} className={inputCls} placeholder="jane@company.com" />
      </Field>
      <Field label="Mobile phone" required>
        <Input value={owner.mobile || ""} onChange={(e) => set("mobile", e.target.value)}
          data-testid={`${prefix}-mobile`} className={inputCls} placeholder="(415) 555-0123" />
      </Field>
      <Field label="Ownership %">
        <Input type="number" min="0" max="100" value={owner.ownership_pct || ""}
          onChange={(e) => set("ownership_pct", e.target.value)} data-testid={`${prefix}-pct`}
          className={inputCls} placeholder="e.g., 75" />
      </Field>
      <Field label="SSN (last 4 only)" hint="Encrypted at rest" required>
        <Input value={owner.ssn_last4 || ""} maxLength={4}
          onChange={(e) => set("ssn_last4", e.target.value.replace(/\D/g, "").slice(0, 4))}
          data-testid={`${prefix}-ssn`} className={inputCls} placeholder="1234" />
      </Field>
      <Field label="Date of birth" required>
        <Input type="date" value={owner.dob || ""} onChange={(e) => set("dob", e.target.value)}
          data-testid={`${prefix}-dob`} className={inputCls} />
      </Field>
      <Field label="Home address" required>
        <Input value={owner.home_address || ""} onChange={(e) => set("home_address", e.target.value)}
          data-testid={`${prefix}-home`} className={inputCls} placeholder="Street, City, State ZIP" />
      </Field>
      <Field label="Estimated credit score">
        <Input type="number" min="300" max="850" value={owner.credit_score || ""}
          onChange={(e) => set("credit_score", e.target.value)} data-testid={`${prefix}-fico`}
          className={inputCls} placeholder="700" />
      </Field>
    </div>
  );
}

export default function NewApplication() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [addSecondOwner, setAddSecondOwner] = useState(false);
  const [bankFiles, setBankFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef();
  const [form, setForm] = useState({
    product_id: "",
    business_legal_name: "",
    legal_entity: "LLC",
    ein: "",
    business_phone: "",
    website: "",
    business_address: "",
    business_start_date: "",
    premises_status: "Own",
    industry: "",
    has_open_balances: false,
    business_description: "",
    monthly_revenue: "",
    requested_amount: "",
    use_of_funds: [],
    primary_owner: { full_name: user?.name || "", email: user?.email || "" },
    signature_name: "",
    agreement_accepted: false,
  });
  const [secondOwner, setSecondOwner] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      if (r.data[0] && !form.product_id) set("product_id", r.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleUse = (u) => {
    set("use_of_funds", form.use_of_funds.includes(u)
      ? form.use_of_funds.filter((x) => x !== u)
      : [...form.use_of_funds, u]);
  };

  const addFiles = (files) => {
    const list = Array.from(files);
    setBankFiles((cur) => [...cur, ...list]);
  };
  const removeFile = (i) => setBankFiles((cur) => cur.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (!form.agreement_accepted) {
      setErr("You must accept the authorization to submit."); return;
    }
    if (!form.signature_name.trim()) {
      setErr("Please type your full name to sign."); return;
    }
    if (!form.product_id) { setErr("Select a financing program."); return; }
    setBusy(true);
    try {
      const payload = {
        product_id: form.product_id,
        requested_amount: Number(form.requested_amount || 0),
        purpose: form.business_description,
        monthly_revenue: Number(form.monthly_revenue || 0) || undefined,
        annual_revenue: Number(form.monthly_revenue || 0) * 12 || undefined,
        industry: form.industry || undefined,
        business_legal_name: form.business_legal_name,
        legal_entity: form.legal_entity,
        ein: form.ein,
        business_phone: form.business_phone,
        website: form.website || undefined,
        business_address: form.business_address,
        business_start_date: form.business_start_date,
        premises_status: form.premises_status,
        has_open_balances: form.has_open_balances,
        business_description: form.business_description,
        use_of_funds: form.use_of_funds,
        fico: form.primary_owner.credit_score ? Number(form.primary_owner.credit_score) : undefined,
        primary_owner: {
          ...form.primary_owner,
          ownership_pct: form.primary_owner.ownership_pct ? Number(form.primary_owner.ownership_pct) : undefined,
          credit_score: form.primary_owner.credit_score ? Number(form.primary_owner.credit_score) : undefined,
        },
        second_owner: addSecondOwner && secondOwner.full_name
          ? {
              ...secondOwner,
              ownership_pct: secondOwner.ownership_pct ? Number(secondOwner.ownership_pct) : undefined,
              credit_score: secondOwner.credit_score ? Number(secondOwner.credit_score) : undefined,
            }
          : undefined,
        signature_name: form.signature_name.trim(),
        agreement_accepted: true,
      };
      const { data: created } = await api.post("/applications", payload);
      // Upload attached bank statements
      for (const f of bankFiles) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("folder", "Bank Statements");
        fd.append("document_type", "Bank Statement");
        try { await api.post(`/applications/${created.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch { /* keep going */ }
      }
      toast.success("Application submitted. Our team will reach out within 24 hours.");
      nav(`/app/applications/${created.id}`);
    } catch (e2) { setErr(formatApiError(e2)); toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1180px] pb-24" data-testid="new-app-form">
      <header className="mb-12">
        <div className="small-caps text-slate-500">Free · No impact to your credit at this stage</div>
        <h1 className="mt-6 font-semibold text-[64px] leading-[0.95] tracking-tight text-[#1F2937]">
          Financing <span className="italic">application.</span>
        </h1>
        <p className="mt-6 max-w-xl text-[15.5px] leading-[1.7] text-slate-600">
          Share the essentials of your business and the capital you need. A
          Venture Nationals officer reviews every file. Most decisions arrive
          within one business day.
        </p>
      </header>

      <form onSubmit={submit} className="space-y-14">
        <Section
          eyebrow="Program"
          title="Which product are you exploring?"
          description="Not sure? Pick the closest match — your officer can switch you to another program."
        >
          <Field label="Financing program" required>
            <Select value={form.product_id} onValueChange={(v) => set("product_id", v)}>
              <SelectTrigger className={inputCls + " w-full"} data-testid="app-product">
                <SelectValue placeholder="Choose a program" />
              </SelectTrigger>
              <SelectContent>
                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </Section>

        <Section
          eyebrow="The business"
          title="Company details"
          description="The entity applying for funding. All information is held in strict confidence."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Legal business name" required>
              <Input value={form.business_legal_name} onChange={(e) => set("business_legal_name", e.target.value)}
                data-testid="biz-name" className={inputCls} placeholder="Acme Manufacturing LLC" />
            </Field>
            <Field label="Legal entity" required>
              <Select value={form.legal_entity} onValueChange={(v) => set("legal_entity", v)}>
                <SelectTrigger className={inputCls + " w-full"} data-testid="biz-entity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEGAL_ENTITIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Federal tax ID (EIN)" required>
              <Input value={form.ein} onChange={(e) => set("ein", e.target.value)}
                data-testid="biz-ein" className={inputCls} placeholder="12-3456789" />
            </Field>
            <Field label="Business phone" required>
              <Input value={form.business_phone} onChange={(e) => set("business_phone", e.target.value)}
                data-testid="biz-phone" className={inputCls} placeholder="(415) 555-0100" />
            </Field>
            <Field label="Website" hint="Optional">
              <Input value={form.website} onChange={(e) => set("website", e.target.value)}
                data-testid="biz-web" className={inputCls} placeholder="acme.com" />
            </Field>
            <Field label="Founded" required>
              <Input type="date" value={form.business_start_date}
                onChange={(e) => set("business_start_date", e.target.value)}
                data-testid="biz-start" className={inputCls} />
            </Field>
            <Field label="Business address" required>
              <Input value={form.business_address} onChange={(e) => set("business_address", e.target.value)}
                data-testid="biz-addr" className={inputCls} placeholder="Street, City, State ZIP" />
            </Field>
            <Field label="Premises status" required>
              <Select value={form.premises_status} onValueChange={(v) => set("premises_status", v)}>
                <SelectTrigger className={inputCls + " w-full"} data-testid="biz-premises"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PREMISES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Industry" required>
              <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
                <SelectTrigger className={inputCls + " w-full"} data-testid="biz-industry">
                  <SelectValue placeholder="Select an industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Any open advance balances?" required>
              <div className="flex items-center gap-6 h-10">
                <label className="inline-flex items-center gap-2 text-[15px] text-[#1F2937]">
                  <input type="radio" checked={form.has_open_balances === false}
                    onChange={() => set("has_open_balances", false)} data-testid="biz-openbal-no" /> No
                </label>
                <label className="inline-flex items-center gap-2 text-[15px] text-[#1F2937]">
                  <input type="radio" checked={form.has_open_balances === true}
                    onChange={() => set("has_open_balances", true)} data-testid="biz-openbal-yes" /> Yes
                </label>
              </div>
            </Field>
          </div>

          <Field label="Brief business description" required>
            <Textarea rows={3} value={form.business_description}
              onChange={(e) => set("business_description", e.target.value)}
              data-testid="biz-desc"
              className="border-0 border-b border-slate-300 rounded-none px-0 bg-transparent shadow-none font-semibold text-[16px] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#1F2937] resize-none"
              placeholder="What do you do, who do you sell to, and how do you make money?" />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Average monthly revenue (USD)" required>
              <Input type="number" min="0" value={form.monthly_revenue}
                onChange={(e) => set("monthly_revenue", e.target.value)}
                data-testid="biz-mrev" className={inputCls} placeholder="e.g., 85000" />
            </Field>
            <Field label="Capital requested (USD)" required>
              <Input type="number" min="0" value={form.requested_amount}
                onChange={(e) => set("requested_amount", e.target.value)}
                data-testid="biz-amount" className={inputCls} placeholder="e.g., 250000" />
            </Field>
          </div>

          <Field label="Use of funds" hint="Choose all that apply">
            <div className="flex flex-wrap gap-2 pt-2">
              {USE_OF_FUNDS.map((u) => {
                const on = form.use_of_funds.includes(u);
                return (
                  <button
                    type="button" key={u} onClick={() => toggleUse(u)}
                    data-testid={`use-${u.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`px-3 py-1.5 border small-caps text-[11px] transition-colors ${
                      on ? "border-[#1F2937] bg-[#1F2937] text-white" : "border-slate-300 text-slate-600 hover:border-[#1F2937]"
                    }`}
                  >
                    {u}
                  </button>
                );
              })}
            </div>
          </Field>
        </Section>

        <Section
          eyebrow="The owner"
          title="Primary owner"
          description="The individual with the largest equity stake (typically 20%+ of the business)."
        >
          <OwnerForm prefix="owner1" owner={form.primary_owner} onChange={(o) => set("primary_owner", o)} />
        </Section>

        <Section
          eyebrow="Additional owner"
          title="Second owner"
          description="Optional. Add another individual holding 20%+ of the business."
        >
          {!addSecondOwner ? (
            <button
              type="button"
              onClick={() => setAddSecondOwner(true)}
              data-testid="add-second-owner"
              className="inline-flex items-center gap-3 small-caps text-[#1F2937] hover:opacity-70"
            >
              <Plus className="h-3.5 w-3.5" /> Add a second owner
            </button>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="small-caps text-slate-500">Second owner details</div>
                <button
                  type="button"
                  onClick={() => { setAddSecondOwner(false); setSecondOwner({}); }}
                  data-testid="remove-second-owner"
                  className="inline-flex items-center gap-1.5 small-caps text-slate-500 hover:text-[#1F2937]"
                >
                  <X className="h-3.5 w-3.5" /> Remove
                </button>
              </div>
              <OwnerForm prefix="owner2" owner={secondOwner} onChange={setSecondOwner} />
            </div>
          )}
        </Section>

        <Section
          eyebrow="Documents"
          title="Bank statements"
          description="Attach the last four months of business bank statements. Underwriting cannot start without them."
        >
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            data-testid="bank-drop"
            className={`cursor-pointer border border-dashed p-10 text-center transition-colors ${
              drag ? "border-[#1F2937] bg-slate-800/[0.03]" : "border-slate-300 hover:border-[#1F2937]/50"
            }`}
          >
            <Upload className="mx-auto h-6 w-6 text-slate-400" />
            <div className="mt-3 font-semibold text-[18px] text-[#1F2937]">
              Drop files here or click to browse
            </div>
            <div className="mt-2 small-caps text-slate-400 text-[10px]">
              PDF · JPG · PNG · up to 15MB each
            </div>
            <input
              ref={fileRef}
              type="file"
              hidden
              multiple
              accept="application/pdf,image/*"
              onChange={(e) => addFiles(e.target.files)}
              data-testid="bank-file-input"
            />
          </div>
          {bankFiles.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="bank-file-list">
              {bankFiles.map((f, i) => (
                <div key={i} className="flex items-center justify-between border border-slate-200 px-4 py-2 text-[13px]">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                    <span className="truncate text-[#1F2937] font-semibold">{f.name}</span>
                    <span className="small-caps text-slate-400 text-[10px]">{(f.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    data-testid={`bank-file-remove-${i}`}
                    className="text-slate-400 hover:text-[#1F2937]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section
          eyebrow="Consent"
          title="Authorization &amp; e-signature"
          description="Please review the terms and sign to submit."
        >
          <div className="border border-slate-300 bg-slate-50 p-6 text-[13px] leading-[1.7] text-slate-700 max-h-72 overflow-auto">
            <p>
              By signing below I certify that all information provided in this
              application is complete, accurate, and truthful to the best of my
              knowledge.
            </p>
            <p className="mt-3">
              I authorize Venture Nationals and its authorized lending partners to
              obtain business and personal credit reports, verify banking and
              trade references, and share this application and its attachments
              with underwriters and capital providers involved in evaluating this
              request.
            </p>
            <p className="mt-3">
              I consent to receive electronic communications, including phone
              calls, text messages, and emails, from Venture Nationals regarding
              this application and related products. Consent to receive marketing
              messages is not a condition of approval. Standard message and data
              rates may apply.
            </p>
            <p className="mt-3">
              Submitting this application does not initiate a hard credit
              inquiry. A Venture Nationals representative will contact you before
              any hard pull is performed.
            </p>
          </div>

          <Field label="I read and agree to the authorization above." required>
            <label className="inline-flex items-center gap-3 h-10 cursor-pointer text-[15px] text-[#1F2937]">
              <input
                type="checkbox"
                checked={form.agreement_accepted}
                onChange={(e) => set("agreement_accepted", e.target.checked)}
                data-testid="app-consent"
              />
              I have read and agree
            </label>
          </Field>

          <Field label="Signature — type your full legal name" required>
            <Input
              value={form.signature_name}
              onChange={(e) => set("signature_name", e.target.value)}
              data-testid="app-signature"
              className={inputCls + " italic"}
              placeholder="Your full legal name"
            />
          </Field>

          <div className="small-caps text-slate-500 text-[11px]">
            Signed on {new Date().toLocaleDateString()}
          </div>
        </Section>

        {err && (
          <div className="border-l-2 border-red-600 pl-4 py-2 text-[14px] text-red-700" data-testid="app-error">
            {err}
          </div>
        )}

        <div className="border-t border-slate-200 pt-10 flex items-center justify-between flex-wrap gap-6">
          <div className="max-w-md">
            <div className="small-caps text-slate-500 mb-1">Ready when you are</div>
            <div className="text-[13.5px] text-slate-600 leading-[1.6]">
              Submitting does not affect your personal credit. An officer will reach
              out before any hard pull.
            </div>
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="app-submit"
            className="group inline-flex items-center gap-4 disabled:opacity-40"
          >
            <span className="h-[1px] w-16 bg-slate-800 transition-all group-hover:w-24" />
            <span className="small-caps text-[#1F2937] inline-flex items-center gap-2">
              <Check className="h-3.5 w-3.5" />
              {busy ? "Submitting…" : "Submit application"}
            </span>
          </button>
        </div>
      </form>
    </div>
  );
}
